import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { BANNERS_IN_GROUP_LIMIT } from '../../constants';
import { Bem } from '../../service/Bem/Bem';
import { GdAdGroupPrimaryStatus } from '../../typings/graphql-grid';
import { CampaignAndGroupSelectorEntityToSelectType } from '../../typings/state';
import { BetaMark } from '../BetaMark/BetaMark';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';

import './CampaignAndGroupSelector.css';
import * as keyset from './CampaignAndGroupSelector.i18n';
import { CampaignSelector } from './CampaignSelector/CampaignSelector';
import { GroupSelector } from './GroupSelector/GroupSelector';
import { SelectorItems } from './Selector/Selector';

const i18n = i18nFactory(keyset);

export enum SelectorStatus {
    LOADING_CAMPAIGN = 'LOADING_CAMPAIGN',
    LOADING_GROUP = 'LOADING_GROUP',
    GROUP_ERROR = 'GROUP_ERROR',
    CAMPAIGN_ERROR = 'CAMPAIGN_ERROR',
    READY = 'READY'
}

export interface CampaignAndGroupSelectorProps {
    type?: CampaignAndGroupSelectorEntityToSelectType;
    onCancel: () => void;
    onAccept: () => void;
    onAcceptDna: () => void;
    onCampaignSelect: (selectorItems: SelectorItems) => void;
    onCampaignInputChange: (text: string) => void;
    onGroupSelect: (selectorItems: SelectorItems) => void;
    onGroupInputChange: (text: string) => void;
    campaignSelected?: SelectorItems;
    campaignItems?: Array<SelectorItems>;
    selectorStatus: SelectorStatus;
    groupSelected?: SelectorItems;
    groupItems?: Array<SelectorItems>;
    canAddInDnaOnly?: boolean;
    canAddInDna: boolean;
    fullyAvailable: boolean;
}

interface CampaignAndGroupSelectorState {
    groupInput: string;
    campaignInput: string;
}

const BLOCK = 'campaign-and-group-selector';

export class CampaignAndGroupSelector extends React.PureComponent<
    CampaignAndGroupSelectorProps,
    CampaignAndGroupSelectorState
> {
    constructor(props: CampaignAndGroupSelectorProps) {
        super(props);

        this.state = {
            groupInput: '',
            campaignInput: ''
        };
    }

    componentDidUpdate(prevProps: CampaignAndGroupSelectorProps) {
        if (prevProps.type !== this.props.type) {
            this.resetInput();
        }
    }

    render() {
        const { type } = this.props;

        if (type === undefined) {
            return null;
        }

        return (
            <div className={Bem.cls(BLOCK)}>
                <h3 className={Bem.cls(BLOCK, 'title')}>{this.getTitle()}</h3>
                <div className={Bem.cls(BLOCK, 'selector-container')}>
                    {this.getSelectors()}
                </div>
                <div className={Bem.cls(BLOCK, 'buttons')}>
                    {this.getAddButtons()}
                    <ButtonDecorator
                        size="n"
                        view="default"
                        tone="default"
                        theme="pseudo"
                        cls={Bem.cls(BLOCK, 'cancel-button')}
                        text={i18n('cancel-button')}
                        onClick={this.onCancel}
                    />
                </div>
            </div>
        );
    }

    private getAddButtons() {
        const {
            type,
            onAccept,
            onAcceptDna,
            canAddInDna,
            canAddInDnaOnly,
            fullyAvailable
        } = this.props;

        switch (type) {
            case CampaignAndGroupSelectorEntityToSelectType.BANNER:
            case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                return (
                    <>
                        {!canAddInDnaOnly && !fullyAvailable && (
                            <ButtonDecorator
                                size="n"
                                view="default"
                                tone="default"
                                theme="action"
                                cls={Bem.cls(BLOCK, 'add-button')}
                                text={i18n('add-button')}
                                disabled={this.getAcceptButtonStatus()}
                                onClick={onAccept}
                            />
                        )}
                        {canAddInDna && (
                            <ButtonDecorator
                                size="n"
                                view="default"
                                tone="default"
                                theme="action"
                                cls={Bem.cls(BLOCK, 'add-button')}
                                disabled={this.getAcceptButtonStatus()}
                                onClick={onAcceptDna}
                            >
                                {i18n('add-button')}
                                {!canAddInDnaOnly && !fullyAvailable && (
                                    <BetaMark />
                                )}
                            </ButtonDecorator>
                        )}
                    </>
                );
            case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
            case CampaignAndGroupSelectorEntityToSelectType.CAMPAIGN:
            default:
                return (
                    <ButtonDecorator
                        size="n"
                        view="default"
                        tone="default"
                        theme="action"
                        cls={Bem.cls(BLOCK, 'add-button')}
                        text={i18n('add-button')}
                        disabled={this.getAcceptButtonStatus()}
                        onClick={onAccept}
                    />
                );
        }
    }

    private getTitle = () => {
        const { type } = this.props;

        switch (type) {
            case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                return i18n('select-campaigns');
            case CampaignAndGroupSelectorEntityToSelectType.BANNER:
            case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                return i18n('select-campaigns-and-groups');
            default:
                throw new Error(
                    `Неправильный type для CampaignAndGroupSelector - ${type}`
                );
        }
    };

    private getSelectors = () => {
        const {
            type,
            campaignSelected,
            campaignItems,
            groupSelected,
            groupItems,
            onCampaignSelect,
            onGroupSelect,
            selectorStatus
        } = this.props;
        const { groupInput, campaignInput } = this.state;

        switch (type) {
            case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                return (
                    <CampaignSelector
                        entitityType={type}
                        items={campaignItems}
                        inputText={campaignInput}
                        isActive={true}
                        isLoading={
                            SelectorStatus.LOADING_CAMPAIGN === selectorStatus
                        }
                        error={SelectorStatus.CAMPAIGN_ERROR === selectorStatus}
                        itemSelected={campaignSelected}
                        onInputChange={this.onCampaignInputChange}
                        onItemSelect={onCampaignSelect}
                    />
                );
            case CampaignAndGroupSelectorEntityToSelectType.BANNER:
            case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                return (
                    <>
                        <CampaignSelector
                            entitityType={type}
                            items={campaignItems}
                            inputText={campaignInput}
                            isLoading={
                                SelectorStatus.LOADING_CAMPAIGN ===
                                selectorStatus
                            }
                            error={
                                SelectorStatus.CAMPAIGN_ERROR === selectorStatus
                            }
                            isActive={true}
                            itemSelected={campaignSelected}
                            onInputChange={this.onCampaignInputChange}
                            onItemSelect={onCampaignSelect}
                        />
                        <GroupSelector
                            entitityType={type}
                            items={groupItems}
                            isLoading={
                                SelectorStatus.LOADING_GROUP === selectorStatus
                            }
                            error={
                                SelectorStatus.GROUP_ERROR === selectorStatus
                            }
                            inputText={groupInput}
                            itemSelected={groupSelected}
                            isActive={Boolean(campaignSelected)}
                            onInputChange={this.onGroupInputChange}
                            onItemSelect={onGroupSelect}
                        />
                    </>
                );
            default:
                throw new Error(
                    `Unsupported view for campaignAndGroupSelector - ${type}`
                );
        }
    };

    private getAcceptButtonStatus = () => {
        const { type, campaignSelected, groupSelected } = this.props;

        switch (type) {
            case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                return !Boolean(campaignSelected);
            case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                return !(
                    Boolean(campaignSelected) &&
                    Boolean(
                        groupSelected &&
                            groupSelected.adsCount !== undefined &&
                            groupSelected.adsCount < BANNERS_IN_GROUP_LIMIT &&
                            groupSelected.groupStatus !== undefined &&
                            groupSelected.groupStatus !==
                                GdAdGroupPrimaryStatus.ARCHIVED
                    )
                );
            case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                return !(Boolean(campaignSelected) && Boolean(groupSelected));
            default:
                throw new Error(
                    `Unsupported view for campaignAndGroupSelector - ${type}`
                );
        }
    };

    private onGroupInputChange = (groupInput: string) => {
        this.setState({ groupInput });
        this.props.onGroupInputChange(groupInput);
    };

    private onCampaignInputChange = (campaignInput: string) => {
        this.setState({ campaignInput });
        this.props.onCampaignInputChange(campaignInput);
    };

    private onCancel = () => {
        this.resetInput();
        this.props.onCancel();
    };

    private resetInput = () => {
        this.setState({
            campaignInput: '',
            groupInput: ''
        });
    };
}
